import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import Loader from '../../components/Loader';
import useResults from '../../hooks/results/useResults';

import { Navigation } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';

const Results = () => {
  const { id, company } = useParams();
  const { campaignData, loading, error } = useResults(id);
  const [focusedMedia, setFocusedMedia] = useState({
    type: null,
    content: null,
    index: 0,
  });

  useEffect(() => {
    // Set initial focused media when data loads
    if (campaignData) {
      if (campaignData.photos && campaignData.photos.length > 0) {
        setFocusedMedia({
          type: 'image',
          content: campaignData.photos[0],
          index: 0,
        });
      } else if (campaignData.videos && campaignData.videos.length > 0) {
        setFocusedMedia({
          type: 'video',
          content: campaignData.videos[0],
          index: 0,
        });
      }
    }
  }, [campaignData]);

  // Handle error state
  if (error && !loading) {
    return (
      <div className='container error-container'>
        <h2>Error</h2>
        <p>{error}</p>
        <a
          href='/'
          className='btn'>
          Go back to home
        </a>
      </div>
    );
  }

  // Handle media selection
  const handleMediaSelect = (type, content, index) => {
    setFocusedMedia({ type, content, index });
  };

  // Render focused media
  const renderFocusedMedia = () => {
    if (!focusedMedia.type) return <h2>Sin medios disponibles</h2>;

    if (focusedMedia.type === 'image') {
      return (
        <img
          className='selectedSlideImage'
          src={focusedMedia.content}
          alt={`Foto del programa ${focusedMedia.index + 1}`}
        />
      );
    } else if (focusedMedia.type === 'video') {
      return (
        <iframe
          className='selectedSlideVideo'
          src={getYoutubeEmbedUrl(focusedMedia.content)}
          title={`Video del programa ${focusedMedia.index + 1}`}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
        />
      );
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className='results-page'>
          <div className='container'>
            {/* Foundation Header */}
            {campaignData?.foundation?.[0] && (
              <>
                <a
                  href={`/${company}/foundation/${campaignData.foundation[0].name}`}
                  className='foundation-link'>
                  <img
                    src={campaignData.foundation[0].logo}
                    alt={`${campaignData.foundation[0].name} logo`}
                  />
                  {campaignData.foundation[0].name}
                </a>
                <hr />
              </>
            )}

            <div className='data-container'>
              {/* Program Title */}
              {campaignData?.program?.[0]?.name && (
                <h2 className='program-title'>
                  {campaignData.program[0].name}
                </h2>
              )}

              {/* Media Section */}
              <div className='media-container'>
                <div className='focused-media'>{renderFocusedMedia()}</div>

                <div className='media-gallery'>
                  {/* Photos Gallery */}
                  {campaignData?.photos?.length > 0 && (
                    <Swiper
                      slidesPerView={3}
                      spaceBetween={10}
                      navigation={true}
                      loop={campaignData.photos.length > 3}
                      modules={[Navigation]}
                      className='swiper photos-swiper'>
                      {campaignData.photos.map((image, index) => (
                        <SwiperSlide key={`photo-${index}`}>
                          <div
                            className={`thumbnail ${
                              focusedMedia.type === 'image' &&
                              focusedMedia.index === index
                                ? 'active'
                                : ''
                            }`}
                            onClick={() =>
                              handleMediaSelect('image', image, index)
                            }>
                            <img
                              src={image}
                              alt={`Foto del programa ${index + 1}`}
                            />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}

                  {/* Videos Gallery */}
                  {campaignData?.videos?.length > 0 && (
                    <Swiper
                      slidesPerView={3}
                      spaceBetween={10}
                      navigation={true}
                      loop={campaignData.videos.length > 3}
                      modules={[Navigation]}
                      className='swiper videos-swiper'>
                      {campaignData.videos.map((video, index) => (
                        <SwiperSlide key={`video-${index}`}>
                          <div
                            className={`video-thumbnail ${
                              focusedMedia.type === 'video' &&
                              focusedMedia.index === index
                                ? 'active'
                                : ''
                            }`}
                            onClick={() =>
                              handleMediaSelect('video', video, index)
                            }>
                            <img
                              src={`https://img.youtube.com/vi/${getYoutubeID(
                                video
                              )}/mqdefault.jpg`}
                              alt={`Video thumbnail ${index + 1}`}
                            />
                            <div className='play-icon'>▶</div>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}

                  {!campaignData?.photos?.length &&
                    !campaignData?.videos?.length && (
                      <div className='no-media'>
                        <p>Sin medios disponibles</p>
                      </div>
                    )}
                </div>
              </div>

              {/* Financial Information */}
              <div className='resources'>
                <h2>Total recaudado</h2>
                <h2>{formatCurrency(campaignData?.totalRaised || 0)}</h2>
                <p>Número de donantes: {campaignData?.donorsCount || 0}</p>
                <p>
                  Número de donaciones: {campaignData?.payments?.length || 0}
                </p>
                <p>
                  Número de personas beneficiadas:{' '}
                  {campaignData?.benefited || 0}
                </p>
              </div>

              {/* Thank You Section */}
              {campaignData?.gratitudeMessage && (
                <div className='thank'>
                  <h2>¡Gracias!</h2>
                  <p>{campaignData.gratitudeMessage}</p>
                </div>
              )}

              {/* Results Section */}
              {campaignData?.resultsMessage && (
                <div className='results'>
                  <h2>Resultados</h2>
                  <p>{campaignData.resultsMessage}</p>
                </div>
              )}

              {/* Areas of Improvement */}
              {campaignData?.resultsMessage2 && (
                <div className='better'>
                  <h2>Por mejorar</h2>
                  <p>{campaignData.resultsMessage2}</p>
                </div>
              )}

              {/* Bills/Invoices Section */}
              <div className='bills'>
                <h2>Facturas</h2>
                {campaignData?.receipts?.length > 0 ? (
                  <ul className='receipts-list'>
                    {Array.isArray(campaignData.receipts) ? (
                      campaignData.receipts.map((receipt, index) => (
                        <li key={index}>
                          <a
                            href={
                              typeof receipt === 'string'
                                ? receipt
                                : receipt.url
                            }
                            target='_blank'
                            rel='noopener noreferrer'>
                            {typeof receipt === 'string'
                              ? `Factura ${index + 1}`
                              : receipt.name || `Factura ${index + 1}`}
                          </a>
                        </li>
                      ))
                    ) : (
                      <li>
                        <a
                          href={campaignData.receipts}
                          target='_blank'
                          rel='noopener noreferrer'>
                          Factura
                        </a>
                      </li>
                    )}
                  </ul>
                ) : (
                  <p>No hay facturas disponibles</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const getYoutubeID = (url) => {
  if (!url) return null;
  // Extract video ID from YouTube URL
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Convert YouTube URL to proper embed URL
const getYoutubeEmbedUrl = (url) => {
  const videoId = getYoutubeID(url);
  if (!videoId) return url;
  return `https://www.youtube.com/embed/${videoId}`;
};

export default Results;
